import React, { useContext } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import Pill from 'components/Luxkit/Pill'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import WalledContent from 'components/Common/WalledContent'
import { connect } from 'react-redux'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'
import { hasPassedWalledGarden } from 'selectors/accountSelectors'
import config from 'constants/config'
import BedbankFlightPricing from 'components/OfferList/OfferListTiles/BedbankFlightPricing'
import { pluralizeToString } from 'lib/string/pluralize'

interface Props {
  offer: App.BedbankOffer;
  homepageAndVertical?: boolean;
}

interface MappedStateProps {
  hasPassedWall: boolean,
}

function BedbankOfferPriceDetails(props: Props & MappedStateProps) {
  const {
    offer,
    hasPassedWall,
    homepageAndVertical,
  } = props
  const { currentRegionHasFlights } = useContext(GeoContext)
  const showFlightPricingDetails = !offer.walledGarden && currentRegionHasFlights && offer.flights.flightsEnabled && !homepageAndVertical
  const hotelPrice = offer.sell?.price
  const hotelValue = offer.sell?.value ?? 0
  const hotelDiscount = offer.sell?.discount ?? 0
  const exclusivePriceColor = useHotelPriceColour(0)

  const hotelNights = offer.sell?.los ?? 0
  const durationText = `${pluralizeToString('night', hotelNights)} from`
  const noPriceAvailable = hotelPrice === undefined
  const showViewButton = (noPriceAvailable || (hasPassedWall || !offer.walledGarden) && !noPriceAvailable) && !homepageAndVertical

  const wall = (
    <OfferTileLoggedInButtons
      showNewTileOverride
      signUpText="Sign up for free"
      align="none"
      fullWidth={false}
      mdFullWidth={homepageAndVertical}
    />
  )
  return (
    <Group
      direction={homepageAndVertical ? 'vertical' : 'horizontal'}
      horizontalAlign={!hasPassedWall && offer.walledGarden ? 'end' : 'space-between'}
      verticalAlign="end"
    >
      <Group
        desktopHorizontalAlign={homepageAndVertical ? 'center' : 'start'}
        direction="vertical"
        gap={8}
      >
        {!noPriceAvailable &&
          <>
            <div>
              <WalledContent enforced={offer.walledGarden} wall={wall}>
                <Caption variant="large" colour="neutral-two">
                  {durationText}
                </Caption>
                <Heading variant="heading4" colour={exclusivePriceColor}>
                  <FormatCurrency value={hotelPrice} format="roundedDollar" />
                  <Caption variant="medium" as="span" colour="neutral-two">/{offer.saleUnit}</Caption>
                </Heading>
                {hotelDiscount > 0 &&
                  <ValueWrapper variant="large" weight="bold" colour="neutral-one">
                    Valued up to <FormatCurrency value={Math.ceil(hotelValue)} />
                    {hotelDiscount > config.displayDiscountThreshold.bedbank &&
                      <PaletteProvider type="default">
                        <Pill kind="primary" variant="success">-{Math.ceil(hotelDiscount * 100)}%</Pill>
                      </PaletteProvider>
                    }
                  </ValueWrapper>
                }
                <TaxesAndFees
                  taxesPayableAtProperty={false}
                  taxAmount={0}
                  variant="large"
                />
              </WalledContent>
            </div>
            {showFlightPricingDetails && <BedbankFlightPricing />}
          </>
        }
      </Group>
      <CSSBreakpoint min="desktop">
        {showViewButton &&
          <TextButton kind="primary" nonInteractable>
            View
          </TextButton>
        }
      </CSSBreakpoint>
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    hasPassedWall: hasPassedWalledGarden(appState),
  }
})(BedbankOfferPriceDetails)
